import * as arrow from 'apache-arrow';
import * as Yup from 'yup';
import {
  CubeDimensionEnum,
  CubeMeasureEnum,
} from '../../services/data/impl/cube/cubeSlice';

export enum ScanDataEnum {
  detection_id = 'detection_id',
  scan_id = 'scan_id',
  raw_count = 'raw_count',
  count_per_meter = 'count_per_meter',
  estimated_count = 'estimated_count',
  detection_area = 'detection_area',
  detection_length = 'detection_length',
  rowside_id = 'rowside_id',
  bag_id = 'bag_id',
  point_id = 'point_id',
  point_timestamp = 'point_timestamp',
  point_location = 'point_location',
  point_h3_15 = 'point_h3_15',
  point_h3_14 = 'point_h3_14',
  point_img_left_orig = 'point_img_left_orig',
  point_img_left_cc = 'point_img_left_cc',
  point_img_right_orig = 'point_img_right_orig',
  point_img_right_cc = 'point_img_right_cc',
  point_camera_id = 'point_camera_id',
  rowside_num = 'rowside_num',
  view_direction = 'view_direction',
  class_name = 'class_name',
  model_name = 'model_name',
  model_version = 'model_version',
  color_rgb = 'color_rgb',
  color_hsv = 'color_hsv',
  traveled_distance = 'traveled_distance',
  image_captured_length = 'image_captured_length',
  vines_distance_estimation = 'vines_distance_estimation',
  batch_processing_version = 'batch_processing_version',
  elt_version = 'elt_version',
  size_raw_counts = 'size_raw_counts',
  size_estimated_counts = 'size_estimated_counts',
}

export const MapScanDataToCube = {
  [ScanDataEnum.detection_id]: null, // there is no mapping for that
  [ScanDataEnum.scan_id]: CubeDimensionEnum.detectionFactScanId,
  [ScanDataEnum.raw_count]: CubeMeasureEnum.detectionFactRawCount,
  [ScanDataEnum.count_per_meter]: CubeMeasureEnum.detectionFactAvgCountPerMeter,
  [ScanDataEnum.estimated_count]: CubeMeasureEnum.detectionFactEstimatedCount,
  [ScanDataEnum.detection_area]: CubeMeasureEnum.DetectionFactAvgArea,
  [ScanDataEnum.detection_length]: CubeMeasureEnum.DetectionFactAvgLength,
  [ScanDataEnum.rowside_id]: CubeDimensionEnum.detectionFactRowsideId,
  [ScanDataEnum.bag_id]: 'DetectionFact.bagId',
  [ScanDataEnum.point_id]: CubeDimensionEnum.detectionFactPointId,
  [ScanDataEnum.point_timestamp]: CubeDimensionEnum.pointTimestamp,
  [ScanDataEnum.point_location]: CubeDimensionEnum.pointLocation,
  [ScanDataEnum.point_h3_15]: CubeDimensionEnum.pointH315,
  [ScanDataEnum.point_h3_14]: CubeDimensionEnum.pointH314,
  [ScanDataEnum.point_img_left_orig]: 'Point.left_orig',
  [ScanDataEnum.point_img_left_cc]: CubeDimensionEnum.pointLeftColorCorrected,
  [ScanDataEnum.point_img_right_orig]: CubeDimensionEnum.pointRightOrig,
  [ScanDataEnum.point_img_right_cc]: 'Point.right_color_corrected',
  [ScanDataEnum.point_camera_id]: null, // there is no mapping for that
  [ScanDataEnum.rowside_num]: CubeDimensionEnum.rowsideRowNum,
  [ScanDataEnum.view_direction]: CubeDimensionEnum.rowsideViewDirection,
  [ScanDataEnum.class_name]: CubeDimensionEnum.className,
  [ScanDataEnum.model_name]: CubeDimensionEnum.modelName,
  [ScanDataEnum.model_version]: CubeDimensionEnum.modelVersion,
  [ScanDataEnum.color_rgb]: 'DetectionFact.hasColorRgb',
  [ScanDataEnum.color_hsv]: CubeMeasureEnum.DetectionFactHasColorHSV,
  [ScanDataEnum.traveled_distance]:
    CubeMeasureEnum.detectionFactTraveledDistance,
  [ScanDataEnum.image_captured_length]:
    CubeMeasureEnum.detectionFactImageCapturedLength,
  // [ScanDataEnum.vines_distance_estimation]: // TODO: add mapping
  // [ScanDataEnum.batch_processing_version]: // TODO: add mapping
  // [ScanDataEnum.elt_version]: // TODO: add mapping
  // [ScanDataEnum.size_raw_counts]: // TODO: add mapping
  // [ScanDataEnum.size_estimated_counts]: // TODO: add mapping
};

// Define a validation schema for ScanData objects using Yup. Each property in ScanData
// objects must adhere to the validation constraints defined here.
const scanDataSchema = Yup.object().shape({
  detection_id: Yup.string().required(),
  scan_id: Yup.string().required(),
  raw_count: Yup.number().required().nullable(),
  count_per_meter: Yup.number().required().nullable(),
  estimated_count: Yup.number().required().nullable(),
  detection_area: Yup.number().nullable(),
  detection_length: Yup.number().nullable(),
  rowside_id: Yup.string().required(),
  bag_id: Yup.string().required().nullable(),
  point_id: Yup.string().required(),
  point_timestamp: Yup.string().required(),
  point_location: Yup.string().required(),
  point_h3_15: Yup.string().required(),
  point_h3_14: Yup.string().required(),
  point_img_left_orig: Yup.string().required(),
  point_img_left_cc: Yup.string(),
  point_img_right_orig: Yup.string(),
  point_img_right_cc: Yup.string(),
  point_camera_id: Yup.string().required().nullable(),
  rowside_num: Yup.number().required(),
  view_direction: Yup.string().required(),
  class_name: Yup.string().required(),
  model_name: Yup.string().required(),
  model_version: Yup.string().required(),
  color_rgb: Yup.string().nullable(),
  color_hsv: Yup.string().nullable(),
  traveled_distance: Yup.number().nullable(),
  image_captured_length: Yup.number().nullable(),
  vines_distance_estimation: Yup.number().nullable(),
  batch_processing_version: Yup.string().nullable(),
  elt_version: Yup.string().nullable(),
  size_raw_counts: Yup.object().nullable(), // TODO: Create a schema for this object once its structure is known
  size_estimated_counts: Yup.object().nullable(), // TODO: Create a schema for this object once its structure is known
});

// The type ScanData is inferred from the Yup validation schema.
export type ScanData = Yup.InferType<typeof scanDataSchema>;

// scanDataKeys, ScanData schema, ArrowScanData type, and the arrowToScanData conversion function must be kept in sync
export type ArrowScanData = {
  detection_id: arrow.Utf8;
  scan_id: arrow.Utf8;
  raw_count: arrow.Float64;
  count_per_meter: arrow.Float64;
  estimated_count: arrow.Float64;
  detection_area: arrow.Utf8;
  detection_length: arrow.Float64;
  rowside_id: arrow.Utf8;
  bag_id: arrow.Utf8;
  point_id: arrow.Utf8;
  point_timestamp: arrow.Utf8;
  point_location: arrow.Utf8;
  point_h3_15: arrow.Utf8;
  point_h3_14: arrow.Utf8;
  point_img_left_orig: arrow.Utf8;
  point_img_left_cc: arrow.Utf8;
  point_img_right_orig: arrow.Utf8;
  point_img_right_cc: arrow.Utf8;
  point_camera_id: arrow.Utf8;
  rowside_num: arrow.Int32;
  view_direction: arrow.Utf8;
  class_name: arrow.Utf8;
  model_name: arrow.Utf8;
  model_version: arrow.Utf8;
  color_rgb: arrow.Utf8;
  color_hsv: arrow.Utf8;
  traveled_distance: arrow.Float64;
  image_captured_length: arrow.Float64;
  vines_distance_estimation: arrow.Float64;
  batch_processing_version: arrow.Utf8;
  elt_version: arrow.Utf8;
  size_raw_counts: arrow.Struct; // TODO: Create a schema for this object once its structure is known
  size_estimated_counts: arrow.Struct; // TODO: Create a schema for this object once its structure is known
};

// Must be keys of ScanData!!!!!!!!!!!
export enum ScanDataMeasureEnum {
  count_per_meter = ScanDataEnum.count_per_meter,
  detection_area = ScanDataEnum.detection_area,
  detection_length = ScanDataEnum.detection_length,
  color_hsv = ScanDataEnum.color_hsv,
}

// Define a mapping between CubeMeasureEnum and ScanDataMeasureEnum
const CubeRanchToScanDataBlockMapping: {
  [key in CubeMeasureEnum]?: ScanDataMeasureEnum;
} = {
  [CubeMeasureEnum.detectionFactAvgCountPerMeter]:
    ScanDataMeasureEnum.count_per_meter,
  [CubeMeasureEnum.DetectionFactAvgArea]: ScanDataMeasureEnum.detection_area,
  [CubeMeasureEnum.DetectionFactAvgLength]:
    ScanDataMeasureEnum.detection_length,
  [CubeMeasureEnum.DetectionFactHasColorHSV]: ScanDataMeasureEnum.color_hsv,
};

// Define a mapping between ScanDataMeasureEnum and CubeMeasureEnum
const ScanDataBlockToCubeRanchMapping: {
  [key in ScanDataMeasureEnum]?: CubeMeasureEnum;
} = {
  [ScanDataMeasureEnum.count_per_meter]:
    CubeMeasureEnum.detectionFactAvgCountPerMeter,
  [ScanDataMeasureEnum.detection_area]: CubeMeasureEnum.DetectionFactAvgArea,
  [ScanDataMeasureEnum.detection_length]:
    CubeMeasureEnum.DetectionFactAvgLength,
  [ScanDataMeasureEnum.color_hsv]: CubeMeasureEnum.DetectionFactHasColorHSV,
};

/**
 * Convert a ranch level CubeMeasureEnum to a block level ScanDataMeasureEnum.
 * @param measure - CubeMeasureEnum value to be converted.
 * @returns Corresponding ScanDataMeasureEnum value or null if mapping fails.
 */
export function convertMeasureCubeRanchToScanDataBlock(
  measure: CubeMeasureEnum
): ScanDataMeasureEnum | null {
  return CubeRanchToScanDataBlockMapping[measure] || null;
}

/**
 * Convert a block level ScanDataMeasureEnum to a ranch level CubeMeasureEnum.
 * @param measure - ScanDataMeasureEnum value to be converted.
 * @returns Corresponding CubeMeasureEnum value or null if mapping fails.
 */
export function convertMeasureScanDataBlockToCubeRanch(
  measure: ScanDataMeasureEnum
): CubeMeasureEnum | null {
  return ScanDataBlockToCubeRanchMapping[measure] || null;
}
